import Phaser from 'phaser';

/**
 * Hard-edge outline pipeline built on Phaser's PreFXPipeline.
 *
 * Why PreFX (not PostFX): PostFX runs on the full-screen framebuffer, so our
 * shader would see the background as "opaque" and the edge-detection logic
 * would find no transparent neighbors. PreFX renders the sprite to its own
 * padded texture with transparent surroundings — exactly what an outline
 * algorithm needs.
 *
 * Why custom shader (not preFX.addGlow): Phaser's Glow accumulates partial
 * alpha from neighboring samples, producing a soft halo. This shader does a
 * binary test — any opaque neighbor within `thickness` texels → solid color,
 * otherwise transparent. Result: crisp 1-pixel stroke, no blur.
 *
 * Dual layer: one pass, one pipeline. The base ring sits closest to the
 * sprite; the optional highlight ring (activeTurn / targeting) sits outside.
 *
 * Per-sprite state: the pipeline instance is shared across every sprite
 * using it, so per-sprite values (highlight color/thickness) are stored on
 * the sprite itself via setData and pushed to uniforms in `onDraw`.
 */

const FRAG_SHADER = `
precision mediump float;

uniform sampler2D uMainSampler;
uniform vec2 uResolution;

// Base outline (always on)
uniform float uBaseThickness;
uniform vec3 uBaseColor;

// Highlight outline (0 thickness = off)
uniform float uHighlightThickness;
uniform vec3 uHighlightColor;

varying vec2 outTexCoord;

/*
 * Anti-aliased outline via max-of-sampled-alpha.
 *
 * Rather than a binary hit test (which produces staircase jaggies on
 * curves), we sample the texture at "thickness" texels in N directions and
 * take the MAX sampled alpha. Because the texture sampler uses linear
 * filtering, samples that land between an opaque and a transparent texel
 * return fractional alpha — giving us sub-pixel smooth edges for free.
 *
 * 16 angular samples is the sweet spot: fewer shows angular banding on
 * curves, more costs perf without visible gain.
 */

float ringAlpha(float distTexels, vec2 texel) {
    float a = 0.0;
    for (float angle = 0.0; angle < 6.2832; angle += 0.3927) {
        vec2 offset = vec2(cos(angle), sin(angle)) * distTexels * texel;
        a = max(a, texture2D(uMainSampler, outTexCoord + offset).a);
    }
    return a;
}

void main() {
    vec4 color = texture2D(uMainSampler, outTexCoord);
    vec2 texel = vec2(1.0) / uResolution;

    // Highlight ring sits at (base + highlight) distance. We only want its
    // contribution in the OUTER annulus — so subtract off the base's reach.
    float baseA = (uBaseThickness > 0.0) ? ringAlpha(uBaseThickness, texel) : 0.0;
    float hlA = 0.0;
    if (uHighlightThickness > 0.0) {
        float outerA = ringAlpha(uBaseThickness + uHighlightThickness, texel);
        // Subtract base coverage: highlight only shows where base already fades.
        hlA = clamp(outerA - baseA, 0.0, 1.0);
    }

    // "Over" compositing from back to front:
    //   highlight ring (farthest) → base ring → sprite pixel (top)
    vec3 rgb = uHighlightColor * hlA;
    float a = hlA;

    rgb = rgb * (1.0 - baseA) + uBaseColor * baseA;
    a = baseA + a * (1.0 - baseA);

    rgb = rgb * (1.0 - color.a) + color.rgb * color.a;
    a = color.a + a * (1.0 - color.a);

    gl_FragColor = vec4(rgb, a);
}
`;

export const OUTLINE_PIPELINE_NAME = 'OutlineFX';

// Keys used on sprite data for per-sprite uniform values.
const DATA_BASE_COLOR = '__outlineBaseColor';
const DATA_BASE_THICK = '__outlineBaseThick';
const DATA_HL_COLOR = '__outlineHlColor';
const DATA_HL_THICK = '__outlineHlThick';

type RGB = [number, number, number];

function hexToRgb(hex: number): RGB {
  return [
    ((hex >> 16) & 0xff) / 255,
    ((hex >> 8) & 0xff) / 255,
    (hex & 0xff) / 255,
  ];
}

export class OutlinePipeline extends Phaser.Renderer.WebGL.Pipelines.PreFXPipeline {
  constructor(game: Phaser.Game) {
    super({
      game,
      name: OUTLINE_PIPELINE_NAME,
      fragShader: FRAG_SHADER,
    });
  }

  /** PreFX hook — called once per sprite render. `this.tempSprite` is set
   *  by PreFXPipeline.batchQuad just before, so we can pull per-sprite
   *  state and push it to uniforms for this render. */
  onDraw(target: Phaser.Renderer.WebGL.RenderTarget): void {
    // `tempSprite` is set by Phaser.PreFXPipeline.batchQuad right before
    // onDraw. Not in the typings yet (Phaser 3.70+), so cast through any.
    const sprite = (this as unknown as { tempSprite: Phaser.GameObjects.Sprite | null }).tempSprite;

    const baseColor = (sprite?.getData(DATA_BASE_COLOR) as RGB | undefined) ?? [0, 0, 0];
    const baseThick = (sprite?.getData(DATA_BASE_THICK) as number | undefined) ?? 1;
    const hlColor = (sprite?.getData(DATA_HL_COLOR) as RGB | undefined) ?? [1, 1, 1];
    const hlThick = (sprite?.getData(DATA_HL_THICK) as number | undefined) ?? 0;

    this.set2f('uResolution', target.width, target.height);
    this.set1f('uBaseThickness', baseThick);
    this.set3f('uBaseColor', baseColor[0], baseColor[1], baseColor[2]);
    this.set1f('uHighlightThickness', hlThick);
    this.set3f('uHighlightColor', hlColor[0], hlColor[1], hlColor[2]);

    this.drawToGame(target);
  }

  // ── Static helpers ───────────────────────────────────────────────

  private static registered = false;

  static register(game: Phaser.Game): void {
    if (OutlinePipeline.registered) return;
    const renderer = game.renderer;
    if (!renderer || !('pipelines' in renderer)) return;
    (renderer as Phaser.Renderer.WebGL.WebGLRenderer).pipelines.add(
      OUTLINE_PIPELINE_NAME,
      new OutlinePipeline(game),
    );
    OutlinePipeline.registered = true;
  }

  /** Apply the outline pipeline to a sprite with a base ring. Replaces the
   *  default MultiPipeline — PreFXPipeline inherits it, so normal sprite
   *  rendering (tints, etc.) still works. Reserves padding so the outline
   *  has transparent pixels to paint into. */
  static applyBase(
    sprite: Phaser.GameObjects.Sprite,
    color = 0x000000,
    thickness = 1,
    padding = 3,
  ): void {
    OutlinePipeline.register(sprite.scene.game);
    if (sprite.preFX) sprite.preFX.setPadding(padding);
    sprite.setData(DATA_BASE_COLOR, hexToRgb(color));
    sprite.setData(DATA_BASE_THICK, thickness);
    sprite.setPipeline(OUTLINE_PIPELINE_NAME);
  }

  /** Set the highlight overlay (active turn, targeting). */
  static setHighlight(sprite: Phaser.GameObjects.Sprite, color: number, thickness = 2): void {
    sprite.setData(DATA_HL_COLOR, hexToRgb(color));
    sprite.setData(DATA_HL_THICK, thickness);
  }

  /** Clear the highlight overlay, keeping the base outline. */
  static clearHighlight(sprite: Phaser.GameObjects.Sprite): void {
    sprite.setData(DATA_HL_THICK, 0);
  }
}
